import { LinkEntryRow } from '@/components/admin/liens/link-entry-row';
import { ReferenceField } from '@/components/admin/reglages/reference-list';
import { RequiredMark } from '@/components/admin/required-mark';
import type { LinkFormDraft } from '@/lib/link-form';
import {
  addLinkRow,
  canSubmitLink,
  editLinkRow,
  linkExperienceLookupOf,
  linkFormProblems,
  removeLinkRow,
} from '@/lib/link-form';
import { LINK_LIFETIME_LABEL } from '@/lib/link-labels';
import type { ExperienceCard, LinkEntryProblem } from '@matter_extractor/shared';
import { LINK_ENTRIES_MAX, LINK_LIFETIMES, LINK_NAME_MAX } from '@matter_extractor/shared';
import { Button, Select } from '@pms-digital/design-system';
import { useState } from 'react';

/**
 * The form of a link (§11.4, planche `ReglagesPartage`) - the SAME one for the
 * creation and for « Modifier ».
 *
 * One component and not two, because §5.1 makes the two gestures write the same
 * three things (« tout est éditable après coup sauf le secret ») and because the
 * entries block is where every rule of this screen lives: written twice, the two
 * copies would have diverged on the refusal before the lot shipped - the
 * argument `reference-list.tsx` makes about the two sections it serves.
 *
 * ## The draft is state, initialised from props and NEVER by an effect
 *
 * The page mounts it with a `key`, so « repartir de zéro » is a REMOUNT - the
 * régime of `involvement-scale.tsx` and of `versioned-editor.tsx`. That is what
 * lets a creation clear itself once it has landed, and a « Modifier » reopen on
 * the base rather than on what was typed before a refusal.
 *
 * ## Two refusals, and the button is greyed by whichever is in hand
 *
 * The local one comes from the experience cards the page already read, so the
 * button greys at the keystroke rather than at the round trip. The server's
 * comes back in a 409 and is the authority - the cards can be a colleague's
 * gesture out of date. They are merged by INDEX, and the server's wins on a row
 * both name: it is the one that just refused the write.
 *
 * While the library is still in flight the local walk is SKIPPED entirely, and
 * that is deliberate: `linkEntryProblems` answers `experience-unknown` for
 * every id it cannot resolve, so running it on an empty list would accuse every
 * row of the form. Same flag, same reason, as `criteriaRead` in the protocol
 * editor.
 */

export interface LinkFormProps {
  readonly initial: LinkFormDraft;
  readonly experiences: readonly ExperienceCard[];
  /** False while the library is in flight - an empty list must not ACCUSE. */
  readonly experiencesRead: boolean;
  readonly submitLabel: string;
  readonly submitting: boolean;
  /** The rows the SERVER refused, empty when it has refused nothing. */
  readonly serverProblems: readonly LinkEntryProblem[];
  /** A transport failure, worded by the page - never a refusal. */
  readonly failure: string | null;
  readonly testId: string;
  readonly onSubmit: (draft: LinkFormDraft) => void;
  /** Present on « Modifier » only: a creation has nothing to go back to. */
  readonly onCancel?: (() => void) | undefined;
}

export function LinkForm({
  initial,
  experiences,
  experiencesRead,
  submitLabel,
  submitting,
  serverProblems,
  failure,
  testId,
  onSubmit,
  onCancel,
}: LinkFormProps) {
  const [draft, setDraft] = useState<LinkFormDraft>(initial);

  const localProblems = experiencesRead
    ? linkFormProblems(draft, linkExperienceLookupOf(experiences))
    : [];
  const problems = mergeProblems(localProblems, serverProblems);
  const armed = canSubmitLink(draft, problems) && !submitting;

  return (
    <form
      data-testid={testId}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (armed) onSubmit(draft);
      }}
      className="space-y-5"
    >
      <div className="max-w-xl">
        <ReferenceField
          label="Nom"
          testId="partage-nom"
          value={draft.name}
          max={LINK_NAME_MAX}
          placeholder="Atelier du 24 septembre"
          onChange={(name) => setDraft((current) => ({ ...current, name }))}
        />
        <p className="pt-1 text-gray-500 text-caption">
          Pour vous : la personne ne le voit jamais.
        </p>
      </div>

      {/* The test id on a wrapper: the package `Select` takes none, its trigger
          is the `combobox` inside. `required` draws the asterisk. */}
      <div data-testid="partage-duree" className="max-w-xs">
        <Select
          label="Durée de vie"
          required
          compact
          value={draft.lifetime}
          disabled={submitting}
          help="À partir de la création. Prolongeable ensuite depuis la liste."
          onValueChange={(value) => {
            /*
              The value arrives as a string and is narrowed HERE, before the
              updater: only a lifetime of the closed list is written.
            */
            const lifetime = LINK_LIFETIMES.find((one) => one === value);
            if (lifetime !== undefined) setDraft((current) => ({ ...current, lifetime }));
          }}
          options={LINK_LIFETIMES.map((lifetime) => ({
            value: lifetime,
            label: LINK_LIFETIME_LABEL[lifetime],
          }))}
        />
      </div>

      <fieldset className="space-y-3">
        <legend className="font-semibold text-gray-800 text-caption">
          Entrées
          <RequiredMark />
        </legend>
        <ul className="space-y-4">
          {draft.entries.map((row, index) => (
            <LinkEntryRow
              // The row's LOCAL key and not its index: two rows may name the
              // same experience under the same tag, and an index key would
              // remount a row under the cursor when one above it goes.
              key={row.key}
              row={row}
              index={index}
              experiences={experiences}
              problem={problems.find((problem) => problem.index === index) ?? null}
              removable={draft.entries.length > 1}
              disabled={submitting}
              onChange={(patch) => setDraft((current) => editLinkRow(current, index, patch))}
              onRemove={() => setDraft((current) => removeLinkRow(current, index))}
            />
          ))}
        </ul>
        <Button
          type="button"
          variant="accent"
          size="sm"
          icon="add"
          data-testid="partage-ajouter-entree"
          disabled={submitting || draft.entries.length >= LINK_ENTRIES_MAX}
          onClick={() => setDraft((current) => addLinkRow(current))}
        >
          Ajouter une entrée
        </Button>
        <p className="text-gray-500 text-caption">
          Une seule entrée : le lien démarre directement. Plusieurs : la page de choix montre le nom
          et la courte description de chacune.
        </p>
      </fieldset>

      {/* Mounted from the first render, empty or not: a live region created at
          the same moment it fills is not read out. */}
      <p aria-live="polite" className="text-rouge-deep text-small">
        {failure}
      </p>

      <div className="flex items-center gap-3">
        <Button type="submit" variant="secondary" data-testid="partage-soumettre" disabled={!armed}>
          {submitLabel}
        </Button>
        {onCancel === undefined ? null : (
          <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
            Annuler
          </Button>
        )}
      </div>
    </form>
  );
}

/**
 * One list of refusals out of two, the SERVER's winning on a row both name.
 *
 * Both are true statements about the same row and the server's is the fresher:
 * it was computed against the base a moment ago, the local one against cards
 * that may be a colleague's gesture old.
 */
function mergeProblems(
  local: readonly LinkEntryProblem[],
  server: readonly LinkEntryProblem[],
): LinkEntryProblem[] {
  const byIndex = new Map<number, LinkEntryProblem>();
  for (const problem of local) byIndex.set(problem.index, problem);
  for (const problem of server) byIndex.set(problem.index, problem);
  return [...byIndex.values()].sort((a, b) => a.index - b.index);
}
